/**
 * SIL-1347 - the buyer verify banner. Handoff section 1's Von Restorff block:
 * the only saturated element on the marketplace, and - because unverified cards
 * carry no primary button - the page's only call to action.
 *
 * Three states, all decided by the ONE buyer-status query:
 *  - verified      -> renders nothing.
 *  - pending       -> the review copy, and NO button (there is nothing to do).
 *  - anything else -> title, body and "Get verified", which opens the modal the
 *                     root's `VerificationGateProvider` already hosts.
 *
 * Status comes from `useBuyerStatus()` / `isBuyerVerified()` (SIL-1346) and the
 * CTA from `useVerificationGate().openVerification`. Both resolve to the same
 * single cache entry, so this adds no second caller of
 * `GET /api/client/buyer-verification` - the thing that hook exists to prevent.
 *
 * While the status is still loading the banner renders nothing rather than
 * assuming unverified: flashing "Exact prices are shared with verified buyers"
 * at an already-verified buyer on every page load is worse than a beat of
 * nothing.
 */
import { LockKeyhole } from 'lucide-react';
import { isBuyerVerified, useBuyerStatus } from '../../hooks/useBuyerStatus';
import { useVerificationGate } from './VerificationGateProvider';
import {
  BUYER_VERIFY_BANNER_BODY,
  BUYER_VERIFY_BANNER_CTA,
  BUYER_VERIFY_BANNER_CTA_CONTINUE,
  BUYER_VERIFY_BANNER_PENDING_BODY,
  BUYER_VERIFY_BANNER_PENDING_TITLE,
  BUYER_VERIFY_BANNER_TITLE,
} from '@siliconx/shared/ui/buyer-listing/copy/buyerVerification';

export function VerifyBanner() {
  const { data, isPending: statusLoading } = useBuyerStatus();
  const { openVerification } = useVerificationGate();
  const status = data?.status;

  if (statusLoading || status === undefined) return null;
  if (isBuyerVerified(status)) return null;

  const pending = status === 'pending';
  const title = pending ? BUYER_VERIFY_BANNER_PENDING_TITLE : BUYER_VERIFY_BANNER_TITLE;
  const body = pending ? BUYER_VERIFY_BANNER_PENDING_BODY : BUYER_VERIFY_BANNER_BODY;

  return (
    <div
      data-testid="verify-banner"
      data-banner-state={pending ? 'pending' : 'unverified'}
      className="flex flex-col gap-3 rounded-[9px] bg-brand-600 px-[22px] py-[18px] text-white shadow-md sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="flex items-start gap-3">
        <LockKeyhole className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
        <div>
          <div className="text-sm font-semibold">{title}</div>
          <div className="mt-1 max-w-2xl text-xs text-white/85">{body}</div>
        </div>
      </div>
      {!pending && (
        <button
          type="button"
          onClick={() => openVerification()}
          className="shrink-0 self-start rounded-md border border-white/30 bg-white px-4 py-2 text-sm font-medium text-brand-700 transition-colors hover:bg-white/90 sm:self-auto"
        >
          {status === 'draft' ? BUYER_VERIFY_BANNER_CTA_CONTINUE : BUYER_VERIFY_BANNER_CTA} →
        </button>
      )}
    </div>
  );
}
